import clsx from "clsx";
import { BiRupee } from "react-icons/bi";
import Input from "@/app/components/ui/Inputs/Input";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm, RegisterOptions } from "react-hook-form";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { Fragment, useEffect, useRef, useState } from "react";
import useFormValidations from "@/app/hooks/useFormValidations";
import ImageUploadComponent from "@/app/components/ui/ImageUploadComponent";
import { useFileToS3LinkGeneratorAPI } from "@/app/hooks/api_hooks/LabReport/useFileToS3LinkGeneratorAPI";
import { useCashClosingWalletDetails } from "@/app/hooks/api_hooks/dayClosing/useCashClosingWalletDetails";
import { useCmsTransactionsActionAPI } from "@/app/hooks/api_hooks/dayClosing/useCmsTransactionsActionAPI";

interface FormValues {
  collection_amount: string;
  name: string;
  mobile: string;
  receipt_url: any | null;
  remarks?: string;
  cms_unique_id?: string;
}

const AddClinicCashComponent = ({ open, setOpen, handleFetchCMSdata }) => {
  const cancelButtonRef = useRef(null);
  const { forOnlyNumbersWithDecimal, forName } = useFormValidations();
  const { execute: createTransaction } = useCmsTransactionsActionAPI();
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const userLogInfo = useAppSelector((state) => state.auth?.user);
  const [loader, setLoader] = useState(false);
  const { execute: uploadToS3 } = useFileToS3LinkGeneratorAPI();
  const { execute: fetchClinicWalletDetails, clinicCashDetails } = useCashClosingWalletDetails();
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);

  const formHook = useForm<FormValues>({
    mode: "onSubmit",
    defaultValues: {},
  });

  const { handleSubmit, register, watch } = formHook;

  const onSubmit = async (data: FormValues) => {
    setLoader(true);
    const payload_json: any = {
      clinic_id: mappedClinicId,
      collection_amount: data.collection_amount,
      remarks: data.remarks,
      cms_unique_id: data.cms_unique_id,
      receipt_url: data.receipt_url,
      cms_person: {
        name: data.name,
        mobile: data.mobile,
      },
      handover_person: {
        name: userLogInfo?.displayName,
        email: userLogInfo?.email,
      },
    };
    createTransaction(payload_json).then(({ status }) => {
      if (status) {
        setLoader(false);
        setOpen(false);
        handleFetchCMSdata(false);
      } else {
        setLoader(false);
      }
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingFiles(true);
    try {
      const result = await uploadToS3(file, "CASH", "FileList");
      if (result.status) {
        formHook.setValue("receipt_url", result.message);
      }
    } catch (error) {
      console.error("File upload error:", error);
    } finally {
      setUploadingFiles(false);
    }
  };

  useEffect(() => {
    fetchClinicWalletDetails(mappedClinicId);
  }, []);

  useEffect(() => {
    if (clinicCashDetails?.cms_payable_amount) {
      formHook.setValue("collection_amount", clinicCashDetails?.cms_payable_amount);
    }
  }, [clinicCashDetails]);

  const isValid = () => {
    const amount = Number(formHook.watch("collection_amount"));
    const mobile = formHook.watch("mobile");
    const name = formHook.watch("name");
    const receiptUrl = formHook.watch("receipt_url");
    const unique_row_id = formHook.watch("cms_unique_id");
    return amount > 0 && !!mobile && !!name && !!receiptUrl && !!unique_row_id;
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => setOpen(false)}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-xl">
                <div className="bg-white">
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="inline-block align-bottom bg-white text-left overflow-hidden transform transition-all sm:my-4 sm:align-middle w-full"
                  >
                    <div className="px-4 flex justify-between items-center pb-2">
                      <h2 className="text-sm font-semibold text-gray-800">Clinic Cash Handover to CMS</h2>
                      <p className="bg-gray-200 flex justify-center items-center h-6 w-6 rounded-full">
                        <XMarkIcon className="h-4 w-4 cursor-pointer" onClick={() => setOpen(false)} />
                      </p>
                    </div>
                    <div className="px-4 py-2">
                      <div className="grid grid-cols-10 mb-1.5 items-center">
                        <label className="col-span-4 text-xs font-medium text-gray-700 mb-1">Amount to be given</label>
                        <div className="col-span-6">
                          <p className="text-black font-semibold text-center flex bg-[#F3F3F3] p-2.5 items-center rounded-lg">
                            <BiRupee className="w-4 h-4" />
                            {formHook.watch("collection_amount") || 0}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-10 mb-1.5 items-center">
                        <label className="col-span-4 text-xs font-medium text-gray-700 mb-1">CMS Executive Name</label>
                        <div className="col-span-6">
                          <Input
                            className="text-xs"
                            maxLength={20}
                            placeholder="Enter name"
                            register={formHook.register("name", {
                              required: true,
                              ...(forName.validations as RegisterOptions<FormValues, "name">),
                            })}
                            fieldError={formHook.formState.errors.name}
                            errorMessages={[forName.errors, { message: "", type: "required" }]}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-10 mb-1.5 items-center">
                        <label className="col-span-4 text-xs font-medium text-gray-700 mb-1">CMS Unique ID</label>
                        <div className="col-span-6">
                          <Input
                            className="text-xs"
                            maxLength={20}
                            placeholder="Enter unique id"
                            register={formHook.register("cms_unique_id", {
                              required: true,
                              ...(forName.validations as RegisterOptions<FormValues, "cms_unique_id">),
                            })}
                            fieldError={formHook.formState.errors.cms_unique_id}
                            errorMessages={[forName.errors, { message: "", type: "required" }]}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-10 mb-1.5 items-center">
                        <label className="col-span-4 text-xs font-medium text-gray-700 mb-1">
                          CMS Executive Mobile
                        </label>
                        <div className="col-span-6">
                          <Input
                            className="text-xs"
                            maxLength={10}
                            placeholder="Enter Mobile"
                            register={formHook.register("mobile", {
                              required: true,
                              ...(forOnlyNumbersWithDecimal.validations as RegisterOptions<FormValues, "mobile">),
                            })}
                            fieldError={formHook.formState.errors.mobile}
                            errorMessages={[forOnlyNumbersWithDecimal.errors, { message: "", type: "required" }]}
                          />
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="mt-1 flex items-center justify-start mb-3">
                          {uploadingFiles ? (
                            <div className="flex items-center justify-center h-32 w-32 border rounded-lg">
                              <svg
                                className="animate-spin h-6 w-6 text-gray-600"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                ></path>
                              </svg>
                            </div>
                          ) : watch("receipt_url") ? (
                            <div className="mt-3 relative inline-block">
                              <img
                                src={watch("receipt_url")}
                                alt="Receipt Preview"
                                className="max-h-32 rounded-lg border"
                              />
                              <button
                                type="button"
                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-opacity-80"
                                onClick={() => formHook.setValue("receipt_url", null)}
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <ImageUploadComponent
                              handleFileChange={handleFileUpload}
                              heading="Click to Upload Receipt"
                              text="or drag and drop PDF,JPEG,PNG up to 5MB"
                              data={watch("receipt_url")}
                              required={true}
                            />
                          )}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <textarea
                          rows={2}
                          className={clsx("rounded-md w-full border border-gray-300 text-sm p-2")}
                          placeholder="Add remarks if any..."
                          {...register("remarks", {
                            minLength: { value: 3, message: "" },
                          })}
                        />
                      </div>
                    </div>
                    <div className="px-4 pt-0 border-gray-200 w-full gap-3">
                      <button
                        disabled={!isValid() || loader}
                        type="submit"
                        className={clsx(
                          "w-full py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2",
                          {
                            "bg-[#939393] text-white": !isValid() || loader,
                            "bg-black text-white": isValid() && !loader,
                          },
                        )}
                      >
                        {loader ? (
                          <>
                            <svg
                              className="animate-spin h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              ></path>
                            </svg>
                            Loading..
                          </>
                        ) : (
                          "Handover"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default AddClinicCashComponent;
